import { cn } from '@/ui/cn';
import { Skeleton, SkeletonRegion } from '@/ui/Skeleton';

/**
 * What the shop and the marketplace show while a page's data is on its way:
 * grey shapes where the real content will land, so the page does not jump
 * when it arrives. Each mirrors the layout of the page it stands in for -
 * keep them in step when a page's layout changes.
 */

/** One product card (ProductCard): photo, two lines of name, seller, price. */
export function ProductCardSkeleton() {
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-surface md:rounded-card">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="flex flex-1 flex-col gap-1.5 p-2.5 md:p-3">
        <Skeleton className="h-3.5 w-11/12" />
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-3 w-2/5" />
        <Skeleton className="mt-2 h-5 w-1/2" />
      </div>
    </div>
  );
}

/** A grid of product cards; pass the grid classes of the page it stands in for. */
export function ProductGridSkeleton({ count = 8, className }) {
  return (
    <SkeletonRegion label="products" className={cn('grid', className)}>
      {Array.from({ length: count }, (_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </SkeletonRegion>
  );
}

/**
 * The shop's home page sections that wait on the showcase: the category
 * circles (a row of tiles on wider screens) and a product rail. Built from
 * site.css's own classes so phones get the same sideways rails.
 */
export function ShowcaseSkeleton() {
  return (
    <>
      <section className="section" id="categories" aria-hidden="true">
        <div className="site-container">
          <SectionHeadSkeleton />
          <div className="cat-grid">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="cat-card cat-skel">
                <Skeleton className="cat-skel-img" />
                <div className="cat-info">
                  <Skeleton className="mx-auto h-3 w-3/4 md:mx-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <SkeletonRegion as="section" label="products" className="section soft">
        <div className="site-container">
          <SectionHeadSkeleton />
          <div className="product-grid product-rail">
            {Array.from({ length: 4 }, (_, i) => (
              <article key={i} className="product-card">
                <Skeleton className="aspect-square w-full rounded-none" />
                <div className="space-y-2 p-3">
                  <Skeleton className="h-3.5 w-11/12" />
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="mt-3 h-5 w-1/2" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </SkeletonRegion>
    </>
  );
}

function SectionHeadSkeleton() {
  return (
    <div className="mb-4 space-y-2 md:mb-8">
      <Skeleton className="h-6 w-44 md:h-8 md:w-64" />
      <Skeleton className="h-3.5 w-60 max-md:hidden" />
    </div>
  );
}

/** The product page: gallery beside the title, price box and buttons. */
export function ProductPageSkeleton() {
  return (
    <SkeletonRegion label="product">
      <Skeleton className="hidden h-3 w-40 md:block" />
      <div className="grid gap-4 md:mt-4 md:gap-6 lg:grid-cols-2">
        <div>
          <Skeleton className="-mx-4 aspect-square rounded-none md:mx-0 md:rounded-card" />
          <div className="mt-3 hidden gap-2 md:flex">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-16 w-16 rounded-lg" />
            ))}
          </div>
        </div>
        <div>
          <Skeleton className="h-6 w-11/12 md:h-7" />
          <Skeleton className="mt-2 h-6 w-3/5 md:h-7" />
          <Skeleton className="mt-3 h-4 w-1/3" />
          <div className="mt-3 space-y-2.5 rounded-2xl border border-border bg-surface p-4 md:mt-4 md:rounded-card">
            <Skeleton className="h-8 w-2/5" />
            <Skeleton className="h-3.5 w-3/5" />
            <Skeleton className="h-3.5 w-1/2" />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
          <div className="mt-6 space-y-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** A line in the cart or an order: thumbnail, name, variant, price. */
function LineSkeleton() {
  return (
    <div className="flex gap-3 rounded-2xl border border-border bg-surface p-3 md:rounded-card">
      <Skeleton className="h-20 w-20 shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1 space-y-2 py-0.5">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-3 w-1/3" />
        <div className="flex items-center justify-between gap-3 pt-1">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-8 w-24 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

/** The order-summary card on the right of the cart and checkout. */
function SummarySkeleton() {
  return (
    <div className="h-fit space-y-3 rounded-2xl border border-border bg-surface p-5 md:rounded-card">
      <Skeleton className="h-5 w-32" />
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex justify-between gap-4">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-16" />
        </div>
      ))}
      <div className="flex justify-between gap-4 border-t border-border pt-3">
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-20" />
      </div>
      <Skeleton className="h-11 w-full rounded-xl" />
    </div>
  );
}

function PageTitleSkeleton({ wide }) {
  return (
    <div className="space-y-2">
      <Skeleton className={cn('h-7', wide ? 'w-56' : 'w-36')} />
      <Skeleton className="h-3.5 w-48" />
    </div>
  );
}

/** The cart: lines on the left, the summary on the right. */
export function CartSkeleton() {
  return (
    <SkeletonRegion label="your cart">
      <PageTitleSkeleton />
      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-3">
          {Array.from({ length: 3 }, (_, i) => (
            <LineSkeleton key={i} />
          ))}
        </div>
        <SummarySkeleton />
      </div>
    </SkeletonRegion>
  );
}

/** Checkout: the steps, the address form, the summary. */
export function CheckoutSkeleton() {
  return (
    <SkeletonRegion label="checkout">
      <Skeleton className="h-7 w-32" />
      <div className="mt-4 flex items-center gap-2">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex flex-1 items-center gap-2">
            <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
            <Skeleton className="h-3 flex-1" />
          </div>
        ))}
      </div>
      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4 rounded-2xl border border-border bg-surface p-4 md:rounded-card md:p-5">
          <Skeleton className="h-4 w-36" />
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-11 w-full rounded-xl" />
              </div>
            ))}
          </div>
        </div>
        <SummarySkeleton />
      </div>
    </SkeletonRegion>
  );
}

/** An order (and its invoice): heading, where it stands, its lines, totals. */
export function OrderSkeleton({ label = 'your order' }) {
  return (
    <SkeletonRegion label={label}>
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-6 w-24 rounded-full" />
      </div>
      <Skeleton className="mt-2 h-3.5 w-56" />
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-border bg-surface p-4 md:rounded-card">
        <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-3.5 w-4/5" />
        </div>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-3">
          {Array.from({ length: 2 }, (_, i) => (
            <LineSkeleton key={i} />
          ))}
        </div>
        <SummarySkeleton />
      </div>
    </SkeletonRegion>
  );
}

/** A list of orders: code and date on the left, status and total on the right. */
export function OrderRowsSkeleton({ rows = 4 }) {
  return (
    <SkeletonRegion label="your orders" as="div" className="divide-y divide-border">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3.5">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-6 w-24 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/**
 * The whole storefront while it finds out which shop it is: a header bar,
 * the search, a grid of cards and the phone's tab bar. Shared by the shop
 * and the marketplace, so it uses neither one's styles.
 */
export function StorefrontShellSkeleton() {
  return (
    <SkeletonRegion label="the store" className="min-h-screen bg-white">
      <div className="border-b border-border">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 md:h-16 md:px-6">
          <Skeleton className="h-9 w-9 rounded-xl" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="ml-auto h-9 w-9 rounded-full" />
          <Skeleton className="h-9 w-9 rounded-full" />
        </div>
        <div className="px-4 pb-3 md:hidden">
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-5 md:px-6 md:py-8">
        <Skeleton className="aspect-[2/1] w-full rounded-2xl md:aspect-[4/1]" />
        <Skeleton className="mt-6 h-6 w-44" />
        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 flex h-16 items-center justify-around border-t border-border bg-white md:hidden">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-6 w-6 rounded-lg" />
        ))}
      </div>
    </SkeletonRegion>
  );
}
